import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Checkbox } from "@/components/ui/checkbox";
import Select, { MultiValue } from "react-select";
import toast from "react-hot-toast";
import {
  MenuItemOption,
  MenuItemOptionValue,
  ValueDependentPrice,
} from "@/redux/services/menu/menu.type";
import { useGetMenuItemOptionsQuery } from "@/redux/services/menu/menu.api";
import {
  useCreateFormulaComponentItemOptionMutation,
  useDeleteFormulaComponentItemOptionMutation,
} from "@/redux/services/menu/menu_formula.api";
import {
  MenuFormulaComponentItem,
  FormulaComponentItemOption,
  FormulaComponentItemOptionPriceFormData,
} from "@/redux/services/menu/menu_formula.type";
import {
  convertPriceFromCent,
  convertPriceToCent,
} from "@/lib/utils";
import { logger } from "@/services/Logger";
import { toastApiError } from "@/lib/apiErrors";

function extractIdFromIri(iri: string): string | undefined {
  if (!iri || typeof iri !== "string") return undefined;
  const parts = iri.split("/");
  return parts[parts.length - 1];
}

interface SelectedOption {
  label: string;
  value: string;
  isTemplate?: boolean;
  dependsOnOption?: MenuItemOption | null;
  menuItemOptionValues?: MenuItemOptionValue[];
  raw?: MenuItemOption;
}

type PriceModifiersMap = {
  [optionId: string]: { [valueId: string]: number };
};

interface ComponentItemOptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  componentItem: MenuFormulaComponentItem;
  onSuccess?: () => void;
}

const ComponentItemOptionModal: React.FC<ComponentItemOptionModalProps> = ({
  isOpen,
  onClose,
  componentItem,
  onSuccess,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
  const [optionPriceModifiers, setOptionPriceModifiers] =
    useState<PriceModifiersMap>({});
  const [optionSettings, setOptionSettings] = useState<{
    [optionId: string]: { isRequired: boolean; maxSelections: number };
  }>({});
  const [isSaving, setIsSaving] = useState(false);

  const { data: menuItemOptionsData } = useGetMenuItemOptionsQuery({});
  const [createOption] = useCreateFormulaComponentItemOptionMutation();
  const [deleteOption] = useDeleteFormulaComponentItemOptionMutation();

  const itemName =
    componentItem.name ?? componentItem.product?.title ?? "Sans nom";

  useEffect(() => {
    if (isOpen) {
      initFromExistingOptions();
    }
  }, [isOpen, componentItem, menuItemOptionsData]);

  const initFromExistingOptions = () => {
    const existingOptions = componentItem.itemOptions ?? [];

    if (existingOptions.length === 0) {
      setSelectedOptions([]);
      setOptionPriceModifiers({});
      setOptionSettings({});
      return;
    }

    const options: SelectedOption[] = [];
    const priceModifiers: PriceModifiersMap = {};
    const settings: {
      [optionId: string]: { isRequired: boolean; maxSelections: number };
    } = {};

    existingOptions.forEach((opt: FormulaComponentItemOption) => {
      // menuItemOption may be a string IRI, a partial object, or a full object
      const raw = opt.menuItemOption;
      const optionId =
        typeof raw === "object" && raw !== null
          ? raw.id
          : extractIdFromIri(raw as unknown as string);
      if (!optionId) return;

      // Always prefer the full object from the fetched options list
      const fromApi = menuItemOptionsData?.results?.find(
        (o: MenuItemOption) => o.id === optionId
      );
      const resolved =
        fromApi ??
        (typeof raw === "object" && raw?.title ? raw : null);
      if (!resolved) return;

      options.push({
        value: resolved.id,
        label: resolved.title,
        isTemplate: resolved.isTemplate,
        dependsOnOption: resolved.dependsOnOption,
        menuItemOptionValues: resolved.menuItemOptionValues,
        raw: resolved,
      });

      settings[resolved.id] = {
        isRequired: opt.isRequired,
        maxSelections: opt.maxSelections,
      };

      if (opt.itemOptionPrices?.length > 0) {
        const valuePrices: { [valueId: string]: number } = {};
        opt.itemOptionPrices.forEach((price) => {
          const valueId =
            typeof price.menuItemOptionValue === "object"
              ? price.menuItemOptionValue.id
              : extractIdFromIri(price.menuItemOptionValue as unknown as string) ?? "";
          if (valueId) {
            valuePrices[valueId] = convertPriceFromCent(price.priceModifier);
          }
        });
        priceModifiers[resolved.id] = valuePrices;
      }
    });

    setSelectedOptions(options);
    setOptionPriceModifiers(priceModifiers);
    setOptionSettings(settings);
  };

  const getOptionSelectItems = (): SelectedOption[] => {
    if (!menuItemOptionsData?.results) return [];

    return menuItemOptionsData.results.map((option: MenuItemOption) => ({
      value: option.id,
      label: option.title,
      isTemplate: option.isTemplate,
      dependsOnOption: option.dependsOnOption,
      menuItemOptionValues: option.menuItemOptionValues,
      raw: option,
    }));
  };

  const handleOptionChange = (newValue: MultiValue<SelectedOption>) => {
    const typedNewValue = newValue as SelectedOption[];
    setSelectedOptions(typedNewValue);

    // Update price modifiers
    const updatedModifiers: PriceModifiersMap = { ...optionPriceModifiers };
    typedNewValue.forEach((option) => {
      if (
        !updatedModifiers[option.value] &&
        option.menuItemOptionValues &&
        !option.isTemplate &&
        !option.dependsOnOption
      ) {
        const valuePrices: { [valueId: string]: number } = {};
        option.menuItemOptionValues.forEach((value) => {
          valuePrices[value.id] = 0;
        });
        updatedModifiers[option.value] = valuePrices;
      }
    });
    Object.keys(updatedModifiers).forEach((optionId) => {
      if (!typedNewValue.some((option) => option.value === optionId)) {
        delete updatedModifiers[optionId];
      }
    });
    setOptionPriceModifiers(updatedModifiers);

    // Update settings
    const newSettings = { ...optionSettings };
    typedNewValue.forEach((option) => {
      if (!newSettings[option.value]) {
        newSettings[option.value] = {
          isRequired: false,
          maxSelections: getDefaultMaxSelections(
            option.raw?.type,
            option.raw?.menuItemOptionValues?.length
          ),
        };
      }
    });
    Object.keys(newSettings).forEach((optionId) => {
      if (!typedNewValue.some((option) => option.value === optionId)) {
        delete newSettings[optionId];
      }
    });
    setOptionSettings(newSettings);
  };

  const handlePriceModifierChange = (
    optionId: string,
    valueId: string,
    price: number
  ) => {
    setOptionPriceModifiers((prev) => ({
      ...prev,
      [optionId]: {
        ...prev[optionId],
        [valueId]: price,
      },
    }));
  };

  const handleIsRequiredChange = (optionId: string, isRequired: boolean) => {
    setOptionSettings((prev) => ({
      ...prev,
      [optionId]: { ...prev[optionId], isRequired },
    }));
  };

  const handleMaxSelectionsChange = (
    optionId: string,
    maxSelections: number
  ) => {
    setOptionSettings((prev) => ({
      ...prev,
      [optionId]: { ...prev[optionId], maxSelections },
    }));
  };

  const getMinSelections = (type?: string): number => {
    switch (type) {
      case "radio":
      case "select":
        return 1;
      case "checkbox":
        return 0;
      default:
        return 1;
    }
  };

  const getMaxSelections = (
    type?: string,
    optionValuesLength?: number
  ): number => {
    switch (type) {
      case "radio":
        return 1;
      case "select":
      case "checkbox":
        return optionValuesLength ?? 1;
      default:
        return optionValuesLength ?? 1;
    }
  };

  const getDefaultMaxSelections = (
    type?: string,
    optionValuesLength?: number
  ): number => {
    switch (type) {
      case "radio":
      case "select":
        return 1;
      case "checkbox":
        return optionValuesLength ?? 1;
      default:
        return 1;
    }
  };

  const needsPriceModifiers = (option: SelectedOption): boolean => {
    return !option.isTemplate && !option.dependsOnOption;
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    try {
      const existingOptions = componentItem.itemOptions ?? [];

      // Delete options that were removed
      const selectedOptionIds = new Set(
        selectedOptions.map((o) => o.value)
      );
      const toDelete = existingOptions.filter(
        (opt) => !selectedOptionIds.has(opt.menuItemOption.id)
      );
      for (const opt of toDelete) {
        await deleteOption(opt.id).unwrap();
      }

      // Create/update options
      const existingOptionMap = new Map(
        existingOptions.map((opt) => [opt.menuItemOption.id, opt])
      );

      for (const option of selectedOptions) {
        const existing = existingOptionMap.get(option.value);
        const settings = optionSettings[option.value];

        const priceModifiers: FormulaComponentItemOptionPriceFormData[] = [];
        if (needsPriceModifiers(option) && optionPriceModifiers[option.value]) {
          Object.entries(optionPriceModifiers[option.value]).forEach(
            ([valueId, price]) => {
              priceModifiers.push({
                priceModifier: convertPriceToCent(price),
                menuItemOptionValue: `/api/menu_item_option_values/${valueId}`,
              });
            }
          );
        }

        // Delete existing then recreate (simpler than PATCH for nested prices)
        if (existing) {
          await deleteOption(existing.id).unwrap();
        }

        await createOption({
          menuFormulaComponentItem: `/api/menu_formula_component_items/${componentItem.id}`,
          menuItemOption: `/api/menu_item_options/${option.value}`,
          isRequired: settings?.isRequired ?? false,
          maxSelections: settings?.maxSelections ?? 1,
          itemOptionPrices: priceModifiers,
        }).unwrap();
      }

      toast.success("Options mises à jour avec succès");
      onSuccess?.();
      onClose();
    } catch (error: any) {
      logger.error("ComponentItemOptionModal", "Error saving options:", error);
      toastApiError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderEditablePriceModifiers = (option: SelectedOption) => {
    if (!option.menuItemOptionValues) return null;

    return (
      <div className="pl-4 mt-2 space-y-2 border-l-2 border-gray-200">
        <p className="font-medium text-sm">
          Modificateurs de prix pour {option.label}:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {option.menuItemOptionValues.map((value) => (
            <div key={value.id} className="flex items-center">
              <span className="mr-2 text-sm min-w-[100px]">
                {value.title}:
              </span>
              <Input
                type="number"
                placeholder="0"
                value={optionPriceModifiers[option.value]?.[value.id] ?? 0}
                onChange={(e) =>
                  handlePriceModifierChange(
                    option.value,
                    value.id,
                    parseFloat(e.target.value) || 0
                  )
                }
                variant="secondary"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDependentPriceModifiers = (option: SelectedOption) => {
    if (!option.menuItemOptionValues || !option.dependsOnOption) return null;

    return (
      <div className="space-y-3">
        {option.menuItemOptionValues.map((value) => {
          const hasDependentPrices =
            value.valueDependentPrices && value.valueDependentPrices.length > 0;

          if (hasDependentPrices) {
            return (
              <div key={value.id} className="border-t pt-2">
                <p className="text-sm font-medium">
                  {value.title}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                  {value.valueDependentPrices?.map(
                    (depPrice: ValueDependentPrice) => (
                      <div key={depPrice.id} className="flex items-center">
                        <span className="mr-2 text-sm min-w-[130px]">
                          Pour {depPrice.dependsOnValue.title}:
                        </span>
                        <Input
                          type="number"
                          value={convertPriceFromCent(depPrice.priceModifier)}
                          disabled={true}
                          variant="secondary"
                          className="bg-gray-100"
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          }

          return (
            <div key={value.id} className="flex items-center">
              <span className="mr-2 text-sm min-w-[100px]">
                {value.title}:
              </span>
              <Input
                type="number"
                value={
                  value.basePrice ? convertPriceFromCent(value.basePrice) : 0
                }
                disabled={true}
                variant="secondary"
                className="bg-gray-100"
              />
            </div>
          );
        })}
        <p className="text-xs text-gray-500 italic">
          Option dépendante de "{option.dependsOnOption?.title}" - les prix
          varient selon le choix parent
        </p>
      </div>
    );
  };

  const renderTemplatePriceModifiers = (option: SelectedOption) => {
    if (!option.menuItemOptionValues) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {option.menuItemOptionValues.map((value) => {
          const basePrice = value.basePrice
            ? convertPriceFromCent(value.basePrice)
            : 0;

          return (
            <div key={value.id} className="flex items-center">
              <span className="mr-2 text-sm min-w-[100px]">
                {value.title}:
              </span>
              <Input
                type="number"
                value={basePrice}
                disabled={true}
                variant="secondary"
                className="bg-gray-100"
              />
            </div>
          );
        })}
        {option.isTemplate && (
          <p className="text-xs text-gray-500 italic col-span-2">
            Option modèle - les prix sont fixés globalement
          </p>
        )}
      </div>
    );
  };

  const renderPriceModifiers = (option: SelectedOption) => {
    if (!option.menuItemOptionValues) return null;

    if (needsPriceModifiers(option)) {
      return renderEditablePriceModifiers(option);
    }

    return (
      <div className="pl-4 mt-2 space-y-2 border-l-2 border-gray-200">
        <p className="font-medium text-sm">
          Prix pré-définis pour {option.label} (non modifiables):
        </p>
        {option.dependsOnOption
          ? renderDependentPriceModifiers(option)
          : renderTemplatePriceModifiers(option)}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Options - {itemName}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div>
            <label htmlFor="options" className="text-gray-700 block mb-1">
              Options
            </label>
            <Select
              id="options"
              isMulti
              name="options"
              options={getOptionSelectItems()}
              className="basic-multi-select"
              classNamePrefix="select"
              value={selectedOptions}
              onChange={handleOptionChange}
              placeholder="Sélectionner les options"
            />
          </div>

          {selectedOptions.length > 0 && (
            <div className="space-y-4 text-sm">
              {selectedOptions.map((option) => (
                <div
                  key={option.value}
                  className="bg-gray-50 p-3 rounded border"
                >
                  <div className="mb-4">
                    <h5 className="font-medium mb-3">
                      {option.label} - Configuration
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`required-${option.value}`}
                          checked={
                            optionSettings[option.value]?.isRequired ?? false
                          }
                          onCheckedChange={(checked) =>
                            handleIsRequiredChange(
                              option.value,
                              checked as boolean
                            )
                          }
                        />
                        <Label
                          htmlFor={`required-${option.value}`}
                          label="Option requise"
                          className="cursor-pointer"
                        />
                      </div>
                      <div>
                        <Input
                          label="Sélections maximale"
                          type="number"
                          min={getMinSelections(option.raw?.type)}
                          max={getMaxSelections(
                            option.raw?.type,
                            option.raw?.menuItemOptionValues?.length
                          )}
                          value={
                            optionSettings[option.value]?.maxSelections ??
                            getDefaultMaxSelections(
                              option.raw?.type,
                              option.raw?.menuItemOptionValues?.length
                            )
                          }
                          onChange={(e) =>
                            handleMaxSelectionsChange(
                              option.value,
                              parseInt(e.target.value) || 1
                            )
                          }
                          variant="secondary"
                        />
                      </div>
                    </div>
                  </div>

                  {renderPriceModifiers(option)}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" onClick={handleSubmit} isLoading={isSaving}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComponentItemOptionModal;
